import { computed, effect, inject, Injectable, Signal, signal } from '@angular/core';
import { Game } from '../enum/game.enum';
import { HttpClient } from '@angular/common/http';
import { TranslateService, TranslationObject } from '@ngx-translate/core';
import { environment } from '../../environments/environment';
import { DEFAULT_GAME_CONFIG, GameConfig, gameConfigs } from '../config/game.config';
import { AchievementConfig } from '../model/achievements.config';
import { Language } from '../enum/language.enum';
import { resolveAndStoreLanguage } from '../util/language.util';

export const STYLES_PATH = 'styles/';
export const IMAGES_PATH = 'images';
export const CERTIFICATES_PATH = 'certificates/';
export const CONFIGS_PATH = 'configs/';
export const I18N_PATH = 'i18n/';

@Injectable({ providedIn: 'root' })
export class GameThemeService {
  private readonly translateService = inject(TranslateService);
  private readonly http = inject(HttpClient);
  private readonly currentConfig = signal<GameConfig>(DEFAULT_GAME_CONFIG);
  private readonly currentLang = signal<Language>(Language.EN);
  private currentAchievements: AchievementConfig[] = [];
  private uiConfig: Map<string, string> = new Map<string, string>();

  public init(): void {
    const path = window.location.pathname;
    const game = this.findGameByPath(path);
    this.currentConfig.set(gameConfigs[game] ?? DEFAULT_GAME_CONFIG);

    // Resolve the correct language from localStorage / browser / fallback
    // and seed the signal with it. The effect below will fire immediately
    // with this value, loading the right translation file from the start.
    const availableLanguages = this.currentConfig().languages;
    this.currentLang.set(resolveAndStoreLanguage(availableLanguages));

    // Keep the signal in sync with every subsequent translateService.use() call
    // (e.g. user switches language in the header).
    this.translateService.onLangChange.subscribe((event) => {
      this.currentLang.set(event.lang as Language);
    });

    // Reactively load game-specific translation files whenever the language changes.
    effect(() => {
      const lang = this.currentLang();
      const translationPath = `${this.getAssetPath()}${I18N_PATH}${lang}.json`;

      this.http.get<TranslationObject>(translationPath).subscribe({
        next: (translations) => {
          this.translateService.setTranslation(lang, translations, false);
          this.translateService.use(lang);
        },
        error: (err) => {
          console.error(`Failed to load translations for ${this.currentConfig().game.toString()}:`, err);
        },
      });
    });

    void this.initGameStyles();
    void this.setCurrentAchievements();
  }

  public getCurrentAchievements(): AchievementConfig[] {
    return this.currentAchievements;
  }

  public getLanguages(): Signal<Language[]> {
    return computed(() => this.currentConfig().languages);
  }

  public getConfigsPath(): string {
    return `${this.getAssetPath()}${CONFIGS_PATH}`;
  }

  public getBackendApiUrl(): string {
    return environment.apiUrls[this.currentConfig().game];
  }

  public getConfigHintPath(): string {
    return `${this.getConfigsPath()}hint-config.json`;
  }

  public getImagePath(): string {
    return `${this.getAssetPath()}${IMAGES_PATH}`;
  }

  public hasMovingBackground(): boolean {
    return this.getCurrentGame() === Game.GANDALF;
  }

  public getCertificatePath(): string {
    return `${this.getAssetPath()}${CERTIFICATES_PATH}`;
  }

  public getIconUrl(): string {
    return `${this.getAssetPath()}icons/`;
  }

  public getLevelCount(): number {
    return this.currentConfig().levels;
  }

  public getCurrentGame(): Game {
    return this.currentConfig().game;
  }

  public getCertificateName(): string {
    return this.currentConfig().certificate;
  }

  public getLevels(): number[] {
    return [...Array(this.currentConfig().levels)].map((_, i) => i + 1);
  }

  public getLevelIndicatorIsIcon(): boolean {
    return this.currentConfig().levelIndicatorIsIcon;
  }

  // UI customization
  public getEonUiScheme(): string {
    return this.uiConfig.get('scheme') ?? 'white';
  }

  public getEonUiHeaderScheme(): string {
    return this.uiConfig.get('header_scheme') ?? 'white';
  }

  public getEonUiLinkScheme(): string {
    return this.uiConfig.get('link_scheme') ?? 'white';
  }

  public getEonUiButtonScheme(): string {
    return this.uiConfig.get('button_scheme') ?? 'grey900';
  }

  public getEonUiChatButtonScheme(): string {
    return this.uiConfig.get('button_chat_scheme') ?? 'discreet';
  }

  public getEonUiChatButtonRank(): string {
    return this.uiConfig.get('button_chat_rank') ?? 'primary';
  }

  public getChatBotName(): string {
    return this.currentConfig().botName;
  }

  public getLoginImage(): string {
    const imageName = this.uiConfig.get('login_image');
    if (imageName) {
      return `${this.getImagePath()}/${imageName}`;
    }
    return '';
  }

  public hasLearnings(): boolean {
    return this.currentConfig().hasLearnings;
  }

  public hasLevelIndicatorLine(): boolean {
    return !!this.uiConfig.get('level_indicator_show_line');
  }

  private findGameByPath(path: string): Game {
    if (path.startsWith('/vision0')) {
      return Game.VISION0;
    }
    return Game.GANDALF;
  }

  private getAssetPath(): string {
    return `${this.currentConfig().assetsPath}/`;
  }

  private getThemeStylesheetPath(): string {
    const game = this.currentConfig().game.toLowerCase();
    return `${game}-theme.css`;
  }

  private async initGameStyles(): Promise<void> {
    this.applyThemeScopeAttribute();

    await this.ensureThemeStylesheetExists();
    this.applyGameThemeStylesheet();

    await this.loadEonUiCustomization();
  }

  private async ensureThemeStylesheetExists(): Promise<boolean> {
    const href = this.getThemeStylesheetPath();

    try {
      const response = await fetch(href, { method: 'HEAD' });
      if (!response.ok) {
        console.error(`[GameThemeService] Missing theme stylesheet: ${href} (${response.status} ${response.statusText})`);
        return false;
      }

      const contentType = response.headers.get('content-type') ?? '';
      if (!contentType.includes('text/css')) {
        console.error(
          `[GameThemeService] Theme stylesheet has wrong content-type for ${href}: "${contentType}". ` +
          `This usually means the dev server returned index.html (route rewrite) because the file doesn't exist.`,
        );
        return false;
      }

      return true;
    } catch (error) {
      console.error(`[GameThemeService] Failed to validate theme stylesheet: ${href}`, error);
      return false;
    }
  }

  private applyThemeScopeAttribute(): void {
    document.documentElement.dataset['gameTheme'] = this.currentConfig().game;
  }

  private applyGameThemeStylesheet(): void {
    const href = this.getThemeStylesheetPath();
    const linkId = 'game-theme-stylesheet';

    const existing = document.head.querySelector<HTMLLinkElement>(`link#${linkId}`);
    if (existing) {
      existing.href = href;
      existing.setAttribute('data-game-theme', this.currentConfig().game);
      return;
    }

    const link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-game-theme', this.currentConfig().game);
    document.head.appendChild(link);
  }

  private async loadEonUiCustomization(): Promise<void> {
    const configPath = `${this.getAssetPath()}${STYLES_PATH}ui.json`;

    try {
      const response = await fetch(configPath);
      if (response.ok) {
        const json = await response.json();
        this.uiConfig = new Map(Object.entries(json));
      }
      // return await response.json();
    } catch (error) {
      console.error('Error loading E-ON-UI config:', error);
    }
  }

  private async setCurrentAchievements(): Promise<void> {
    const configPath = `${this.getConfigsPath()}achievements.json`;

    try {
      const response = await fetch(configPath);
      if (response.ok) {
        const data = await response.json();
        if (data && Array.isArray(data.achievements)) {
          this.currentAchievements = data.achievements;
        }
      }
    } catch (error) {
      console.error('Error loading achievements:', error);
    }
  }
}
